import { useEffect } from 'react';

import { useTheme } from '@mui/material';

import IndicatorLoading from '@webclient/components/indicator-loading';
import Modal from '@webclient/components/modal';
import { notification } from '@webclient/components/notification';

import { checkUserAuthType, setSession } from '@webclient/lib/jwt';

import useCreateRefreshToken from '@webclient/hooks/useGetRefreshToken';
import useQueryParams from '@webclient/hooks/useQueryParams';
import useUpdateMe from '@webclient/hooks/useUpdateMe';

import { StripePaymentStatusType } from '@webclient/shared-types/payment';

import { useTranslate } from '@tolgee/react';

type Props = React.PropsWithChildren<{
  width: number | string;
  height: number | string;
}>;

let hasFired = false;

export default function ModalWrapper({ height, width, children }: Props) {
  const { getQueryParamByKey, setQueryParam } = useQueryParams();
  const { handleUpdateMe } = useUpdateMe();
  const theme = useTheme();

  const actionQueryParams = getQueryParamByKey<'secure-account'>('action');

  const subscriptionParams = getQueryParamByKey('subscription');

  const paymentParams = getQueryParamByKey<StripePaymentStatusType>('payment');
  const { t } = useTranslate();
  const wasPaymentSuccessful =
    subscriptionParams === 'success' || paymentParams === 'success';

  const {
    mutateCreateRefreshTokenAsync,
    isRefreshTokenIdle,
    isRefreshTokenPending,
    resetRefreshToken,
  } = useCreateRefreshToken(({ data }) => {
    const token = data.token;
    const authType = checkUserAuthType(token);
    const isTheTokenSecure = !authType?.match(/_fingerprint$/);
    if (isTheTokenSecure && wasPaymentSuccessful) {
      notification.success({
        message: t(
          'response.success.payment',
          'Thank you for your payment! Your subscription is now active.',
        ),
      });
      return;
    }
    setQueryParam('action', 'secure-account');
    notification.success({
      message: 'Thank you for your payment! Your subscription is now active.',
    });
    setSession(token);
    handleUpdateMe(token);
  });

  useEffect(() => {
    if (wasPaymentSuccessful && !hasFired && isRefreshTokenIdle) {
      hasFired = true;
      (async function run() {
        await mutateCreateRefreshTokenAsync();
        resetRefreshToken();
      })();
    }
  }, [
    isRefreshTokenIdle,
    mutateCreateRefreshTokenAsync,
    resetRefreshToken,
    wasPaymentSuccessful,
  ]);

  return (
    <Modal
      keepMounted
      modalSx={{
        zIndex: theme.zIndex.modal + 1,
      }}
      open={actionQueryParams === 'secure-account'}
      width={width}
      height={height}
    >
      {isRefreshTokenPending ? (
        <IndicatorLoading sx={{ margin: 'auto' }} />
      ) : (
        children
      )}
    </Modal>
  );
}
